/**
 * DeepDiveHistory - Panel showing past deep dive analysis results.
 *
 * Fetches history from the server. Click an entry to view the saved result.
 */

import { useState, useEffect, useCallback } from 'react';
import { History, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { API_BASE_URL } from '@/lib/api';

interface HistoryItem {
  id: number;
  ticker: string;
  winning_side: string | null;
  confidence: number | null;
  created_at: string;
}

interface DeepDiveHistoryProps {
  onViewResult: (resultId: number) => void;
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

export function DeepDiveHistory({ onViewResult }: DeepDiveHistoryProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/v1/portfolio/deep-dive/history`, { headers });
      if (res.ok) {
        setHistory(await res.json());
      }
    } catch {
      // Silent fail
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && history.length === 0) {
      fetchHistory();
    }
  }, [isOpen, fetchHistory, history.length]);

  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/v1/portfolio/deep-dive/${id}`, {
        method: 'DELETE',
        headers,
      });
      if (res.ok) {
        setHistory((prev) => prev.filter((h) => h.id !== id));
      }
    } catch {
      // Silent fail
    }
  };

  if (history.length === 0 && !isOpen) return null;

  return (
    <div className="relative">
      <button
        onClick={() => { setIsOpen(!isOpen); if (!isOpen) fetchHistory(); }}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-text-muted hover:text-text-secondary hover:bg-surface-light transition-colors"
      >
        <History className="w-3.5 h-3.5" />
        Past Analyses
        {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-72 bg-surface border border-border rounded-lg shadow-xl z-20 overflow-hidden">
          <div className="max-h-64 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 space-y-2">
                <div className="h-4 w-3/4 animate-pulse bg-surface-light rounded" />
                <div className="h-4 w-1/2 animate-pulse bg-surface-light rounded" />
                <div className="h-4 w-2/3 animate-pulse bg-surface-light rounded" />
              </div>
            ) : history.length === 0 ? (
              <div className="p-4 text-center text-xs text-text-muted">No past analyses</div>
            ) : (
              history.map((item) => (
                <button
                  key={item.id}
                  onClick={() => { onViewResult(item.id); setIsOpen(false); }}
                  className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-surface-light transition-colors text-left border-b border-border/50 last:border-0"
                >
                  <div>
                    <div className="text-sm font-medium text-text-primary">{item.ticker}</div>
                    <div className="text-[10px] text-text-muted">
                      {new Date(item.created_at).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                      })}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.winning_side && (
                      <span className={cn(
                        'text-[10px] font-medium px-1.5 py-0.5 rounded',
                        item.winning_side === 'bull'
                          ? 'bg-green-500/10 text-green-400'
                          : 'bg-red-500/10 text-red-400'
                      )}>
                        {item.winning_side === 'bull' ? 'Bullish' : 'Bearish'}
                      </span>
                    )}
                    {item.confidence && (
                      <span className="text-[10px] text-text-muted">
                        {item.confidence.toFixed(0)}%
                      </span>
                    )}
                    <button
                      onClick={(e) => handleDelete(item.id, e)}
                      className="p-1 text-text-muted hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
